// @ts-nocheck
import { Service, ServiceContextMapKey, WorkflowId } from '@ply-io/ply-front-shared-utilities';
import { EmbeddedAppActionTypes, EmbeddedAppEntry } from './types';
import { ComputedRef, ref } from 'vue';

import { Service, ServiceContextMapKey, WorkflowId } from '@ply-io/ply-front-shared-utilities';

import { useEmailsScraper } from './composables';
import { EmbeddedAppActionTypes, EmbeddedAppEntry } from './types';
import { CreationProcess } from '@/components/CreationWizard/types';
import { GetMappedFields } from '@/components/SideBar/types';
import { IS_EMBEDDED_PARAM } from '@/router/router.const';

interface Client extends Record<string, unknown> {
	on: (event: string, callback: () => void) => void;
	// invoke: (event: string) => void;
} // ZendeskClient

let client: Client; // ReturnType<ZAFClient['init']>
const emails = ref<string[]>([]);
const domains = ref<string[]>([]);
const SLUG = 'zendesk';

interface Ticket {
	comments: Comment[];
}
interface Comment {
	author: {
		email: string;
	};
}

const { scraper } = useEmailsScraper();

const fillEmails = async () => {
	const { ticket }: { ticket: Ticket } = await client.get('ticket');
	emails.value = scraper(JSON.stringify(ticket));
};

enum ContextKeys {
	Ticket = 'ticket',
}

let serviceInstance: ComputedRef<Service | undefined>; // until service is loaded, might be undefined
const GET_MAPPED_FIELDS_CONTEXT_HANDLER: Record<`${ContextKeys}`, () => ReturnType<GetMappedFields>> = {
	[ContextKeys.Ticket]: async () => {
		const { 'ticket.id': id } = await client.get('ticket.id');
		return { ...client._context.account, id };
	},
};
const isInEnum = <T extends string>(value: T, enumObject: { [key: string]: T }): boolean => Object.values(enumObject).includes(value);
const isAllowedContextKey = (key: string): key is ContextKeys => isInEnum(key, ContextKeys);
const APP_BASE_URL = process.env.VUE_APP_APP_URL;
const BASE_EMBEDDED_SERVICE_URL = `${APP_BASE_URL}e/${SLUG}/`;
const getShareWorkflowUrl = (id: WorkflowId) => `${BASE_EMBEDDED_SERVICE_URL}share-workflow/${id}`;
const getEditSetupUrl = (id: WorkflowId) => `${BASE_EMBEDDED_SERVICE_URL}edit-workflow-configuration/${id}`;
const getManageFeaturesUrl = (workspaceId = 'all') => `${APP_BASE_URL}extension/connection/${serviceInstance.value?.id}/${workspaceId}/`;
const openNewModal: (baseUrl: string, params: ConstructorParameters<typeof URLSearchParams>[0]) => Promise<Client> = async (
	baseUrl,
	params
) => {
	const searchParams = new URLSearchParams(params);
	const modalContext = await client.invoke('instances.create', {
		location: 'modal',
		url: `${baseUrl}?${searchParams.toString()}`,
		size: {
			width: '74vw',
			height: '74vh',
		},
	});
	activeModal.value = client.instance(modalContext['instances.create'][0].instanceGuid);
	if (!activeModal.value) {
		throw new Error(`[PLY-ZENDESK] MODAL IS NOT CREATED`);
	}
	return activeModal.value;
};

const activeModal = ref<Awaited<ReturnType<typeof openNewModal>> | null>(null);

let setCreationProcess: (cp: CreationProcess) => void;
const getCurrentURL = () => window.location.href; // TODO - get the url from the zendesk client

export const service: EmbeddedAppEntry = {
	init: (setCP, s): void => {
		serviceInstance = s;
		setCreationProcess = setCP;
		const zafScript = document.createElement('script');
		zafScript.setAttribute('src', 'https://static.zdassets.com/zendesk_app_framework_sdk/2.0/zaf_sdk.min.js');
		zafScript.addEventListener('load', async () => {
			client = ZAFClient.init();
			console.log('zendesk client', client);
			client.on('*.changed', () => {
				fillEmails();
			});
			fillEmails();
		});
		document.head.appendChild(zafScript);
	},
	appProps: {
		allowClose: false,
		showLogo: false,
	},
	appFunctions: {
		async openEditSetup(workflowId) {
			await openNewModal(getEditSetupUrl(workflowId), {});
		},
		async openShareWorkflow(workflowId) {
			await openNewModal(getShareWorkflowUrl(workflowId), {});
		},
		async openSettings() {
			await openNewModal(getManageFeaturesUrl(), {});
		},
		async getMappedFields({ key }: { key?: ServiceContextMapKey }) {
			if (!key) {
				throw new Error(`[PLY-ZENDESK] CONTEXT KEY IS REQUIRED`);
			}
			// if key not in ContextKeys enum
			if (!isAllowedContextKey(key)) {
				throw new Error(`[PLY-ZENDESK] UNKNOWN CONTEXT KEY ${key}`);
			}
			return GET_MAPPED_FIELDS_CONTEXT_HANDLER[key]();
		},
		async startCreateTile() {
			await openNewModal(`${BASE_EMBEDDED_SERVICE_URL}creation-wizard/add-on`, {
				currentUrl: getCurrentURL(),
				slug: SLUG,
				elementType: 'tile',
			});
		},
		async editWorkflow(workflowId, projectId) {
			await openNewModal(`${APP_BASE_URL}space/${projectId}/feature/${workflowId}/?${IS_EMBEDDED_PARAM}=true`, {});
		},
		getCurrentURL,
		closeSidebar() {
			console.log('[zendesk] closeSidebar');
		},
	},
	domains,
	emails,
	iframeListeners: {
		[EmbeddedAppActionTypes.StartCreationProcess]: (cp: CreationProcess) => setCreationProcess(cp),
		[EmbeddedAppActionTypes.Close]: () => {
			console.log('[zendesk] close');
			activeModal.value?.invoke('destroy');
			activeModal.value = null;
		},
	},
};
